import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { ZoomIn, ZoomOut } from 'lucide-react';

interface AvatarEditorProps {
  image: string;
  open: boolean;
  onClose: () => void;
  onSave: (croppedImage: string, file: File) => void;
  borderRadius?: number;
  aspectRatio?: number;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: CropArea,
  borderRadius: number
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Apply border radius
  const radius = (borderRadius / 100) * (pixelCrop.width / 2);
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(pixelCrop.width - radius, 0);
  ctx.quadraticCurveTo(pixelCrop.width, 0, pixelCrop.width, radius);
  ctx.lineTo(pixelCrop.width, pixelCrop.height - radius);
  ctx.quadraticCurveTo(
    pixelCrop.width,
    pixelCrop.height,
    pixelCrop.width - radius,
    pixelCrop.height
  );
  ctx.lineTo(radius, pixelCrop.height);
  ctx.quadraticCurveTo(0, pixelCrop.height, 0, pixelCrop.height - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();
  ctx.clip();

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL('image/png');
};

export const AvatarEditor = ({
  image,
  open,
  onClose,
  onSave,
  aspectRatio = 1,
  borderRadius = 100,
}: AvatarEditorProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null
  );

  const onCropComplete = useCallback(
    (_: unknown, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  /**
   * Converts a data URL to a File object
   * @param {string} dataUrl - The data URL to convert
   * @param {string} filename - The filename to use for the resulting File object
   * @returns {File} The resulting File object
   */
  /*******  74570d11-ed1d-44a3-b67c-e128ca6604f5  *******/
  function dataURLtoFile(dataUrl: string, filename: string) {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(
        image,
        croppedAreaPixels,
        borderRadius
      );
      onSave(croppedImage, dataURLtoFile(croppedImage, 'thumbnail.png'));
      onClose();
    } catch (e) {
      console.error('Error cropping image:', e);
    }
  }, [croppedAreaPixels, image, borderRadius, onSave, onClose]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl z-50">
        <DialogHeader>
          <DialogTitle>Edit Avatar</DialogTitle>
        </DialogHeader>

        <div className="relative h-96 bg-muted rounded-lg overflow-hidden">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={borderRadius === 100 ? 'round' : 'rect'}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            classes={{
              cropAreaClassName:
                borderRadius !== 100 ? `rounded-[${borderRadius}%]` : undefined,
            }}
            style={{
              cropAreaStyle: {
                borderRadius: borderRadius === 100 ? '50%' : `${borderRadius}%`,
              },
            }}
          />
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm mb-2 block">Zoom</Label>
            <div className="flex items-center gap-4">
              <ZoomOut className="h-4 w-4 text-muted-foreground" />
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.1}
                onValueChange={(value) => setZoom(value[0])}
                className="flex-1"
              />
              <ZoomIn className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Avatar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
